import {
  AttendanceCode,
  DATE_FORMAT_SHORT_FNS,
  DATE_FORMAT_WEEK_DAY_MID_FNS,
  DEFAULT_DATE_FORMAT_FNS,
} from '@schooly/api';
import { FC, ReactElement, ReactNode, createContext, useContext } from 'react';

import { SESSION_ORDER } from './constants';
import {
  AttendanceCellClickHandler,
  AttendanceGridCell,
  AttendanceGridColumn,
  AttendanceGridSession,
  AttendanceGridStudent,
  AttendanceGridTrackingPoint,
  AttendanceRow,
  AttendanceRowType,
  AttendanceSidebarFocus,
} from './types';
import { Box, Icon, Stack, Tooltip, Typography } from '@mui/material';
import { Attention2Icon, CheckIcon, Skeleton, TypographyWithOverflowHint } from '@schooly/style';
import { AttendanceCodeCircle } from '@schooly/components/attendance';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { format, parse } from 'date-fns';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { AvatarAuth } from '@schooly/components/avatar-auth';

const CIRCLE_SIZE = 24;

export type AttendanceCellContextValue = {
  codesById: Record<string, AttendanceCode>;
  onCellClick: AttendanceCellClickHandler;
  canViewStudents: boolean;
};

type SessionSlot = {
  key: string;
  code: AttendanceCode | undefined;
  blocked: boolean;
};

type DayCellFrameProps = {
  cell: AttendanceGridCell;
  student: AttendanceGridStudent;
  slots: SessionSlot[];
  trackingPoint?: AttendanceGridTrackingPoint;
  hasIndicator: boolean;
  onCellClick: AttendanceCellClickHandler;
};

const resolveCellFocus = (
  trackingPoint: AttendanceGridTrackingPoint,
): AttendanceSidebarFocus | undefined => {
  if (trackingPoint.session) return { session: trackingPoint.session };
  if (trackingPoint.period) return { period_id: trackingPoint.period.id };
};

type AttendanceDayCellProps = {
  row: AttendanceRow;
  column: AttendanceGridColumn;
};

const SkeletonCell: FC = () => (
  <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
    <Skeleton width={CIRCLE_SIZE} height={CIRCLE_SIZE} sx={{ borderRadius: '50% !important' }} />
  </Stack>
);

const StrokeCell: FC = () => (
  <Box
    sx={(theme) => ({
      width: '100%',
      height: '100%',
      backgroundImage: `repeating-linear-gradient(-45deg, ${theme.palette.common.light3} 0, ${theme.palette.common.light3} 1px, transparent 1px, transparent 6px)`,
    })}
  />
);

const BlockedCircle: FC = () => (
  <Box
    sx={(theme) => ({
      width: CIRCLE_SIZE,
      height: CIRCLE_SIZE,
      borderRadius: '50%',
      backgroundColor: theme.palette.common.grey6,
    })}
  />
);

const resolveSessions = (
  column: AttendanceGridColumn,
  cell: AttendanceGridCell,
): AttendanceGridSession[] => {
  if (cell.sessions?.length) return cell.sessions;

  if (column.sessions.length) {
    return column.sessions
      .map(({ name }) => name)
      .filter((name): name is AttendanceGridSession => name === 'am' || name === 'pm');
  }

  const value = cell.data?.cell;
  if (value) {
    const present = SESSION_ORDER.filter((session) => !!value[session]);
    if (present.length) return present;
  }

  return SESSION_ORDER;
};

export const CellIndicator = () => (
  <Box
    sx={(theme) => ({
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: 1,
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '0 10px 10px 0',
      borderColor: `transparent ${theme.palette.warning.main} transparent transparent`,
    })}
  />
);

const resolveNormalSlots = (
  column: AttendanceGridColumn,
  cell: AttendanceGridCell,
  codesById: Record<string, AttendanceCode>,
): SessionSlot[] => {
  if (column.trackingPoint) {
    const { session, period } = column.trackingPoint;
    let entry = null;
    if (period) {
      entry = cell.data?.cell.periods?.[period.id] ?? null;
    } else if (session) {
      entry = cell.data?.cell[session] ?? null;
    }
    const code = entry?.attendance_code_id ? codesById[entry.attendance_code_id] : undefined;
    return [
      {
        key: period?.id ?? session ?? 'am',
        code,
        blocked: !entry,
      },
    ];
  }

  return resolveSessions(column, cell).map((session) => {
    const entry = cell.data?.cell[session] ?? null;
    const code = entry?.attendance_code_id ? codesById[entry.attendance_code_id] : undefined;
    return { key: session, code, blocked: !entry };
  });
};

const hasCellIndicator = (cell: AttendanceGridCell): boolean => {
  const value = cell.data?.cell;
  if (!value) return false;
  if (value.justification || value.absence_request) return true;

  const entries = [
    ...SESSION_ORDER.map((session) => value[session]),
    ...Object.values(value.periods ?? {}),
  ];
  return entries.some((entry) => !!entry && entry.comments.length > 0);
};

const DayCellFrame: FC<DayCellFrameProps> = ({
  cell,
  student,
  slots,
  trackingPoint,
  hasIndicator,
  onCellClick,
}) => {
  const clickable = cell.editable;

  const open = () => {
    const data = cell.data;
    if (!data) return;

    onCellClick(
      {
        register_id: data.register_id,
        date: data.date,
        relation_id: data.relation_id,
        focus: trackingPoint ? resolveCellFocus(trackingPoint) : undefined,
      },
      student,
    );
  };

  return (
    <Box
      onClick={clickable ? open : undefined}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        userSelect: 'none',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      {hasIndicator && <CellIndicator />}
      <Stack direction="row" px={2} sx={{ width: '100%', height: '100%' }}>
        {slots.map((slot) => (
          <Stack key={slot.key} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            {slot.blocked ? <BlockedCircle /> : <AttendanceCodeCircle code={slot.code} />}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export const AttendanceDayCell: FC<AttendanceDayCellProps> = ({ row, column }) => {
  const { codesById, onCellClick } = useAttendanceCellContext();

  const cell = row.cells[column.key];
  if (!cell) return <Box sx={{ width: '100%', height: '100%' }} />;

  switch (cell.kind) {
    case 'loading':
      return <SkeletonCell />;
    case 'out_of_day':
      return <StrokeCell />;
    case 'blocked':
      return (
        <DayCellFrame
          cell={cell}
          slots={[{ key: 'am', code: undefined, blocked: true }]}
          hasIndicator={false}
          onCellClick={onCellClick}
          student={row.student}
        />
      );
    case 'normal':
      const slots = resolveNormalSlots(column, cell, codesById);
      return (
        <DayCellFrame
          cell={cell}
          slots={slots}
          trackingPoint={column.trackingPoint}
          hasIndicator={hasCellIndicator(cell)}
          onCellClick={onCellClick}
          student={row.student}
        />
      );

    default:
      return null;
  }
};

export const AttendancePrimaryCell: FC<{ row: AttendanceRow }> = ({ row }) => {
  const { canViewStudents } = useAttendanceCellContext();

  if (row.type === AttendanceRowType.Week) {
    return (
      <Stack direction="row" alignItems="center" sx={{ overflow: 'hidden', pl: 2.5 }}>
        <TypographyWithOverflowHint color="common.grey2" noWrap>
          {row.label}
        </TypographyWithOverflowHint>
      </Stack>
    );
  }

  const name = getUserFullName(row.student);

  return (
    <Stack direction="row" gap={1} alignItems="center" sx={{ overflow: 'hidden', pl: 2.5 }}>
      <AvatarAuth user={row.student} />
      {canViewStudents ? (
        <TypographyWithOverflowHint
          variant="h3"
          noWrap
          component={Link}
          to={{ pathname: `/students/${row.student.relation_id}` }}
          sx={{
            color: 'text.primary',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {name}
        </TypographyWithOverflowHint>
      ) : (
        <TypographyWithOverflowHint variant="h3" noWrap color="text.primary">
          {name}
        </TypographyWithOverflowHint>
      )}
    </Stack>
  );
};

const REGISTER_SESSION_TOOLTIP_ID = {
  am: 'school-timetableStructure-registerSession-am',
  pm: 'school-timetableStructure-registerSession-pm',
} as const;

const trackingPointTooltipTitle = (
  trackingPoint: AttendanceGridTrackingPoint | undefined,
  formatMessage: (descriptor: { id: string }) => string,
): string | null => {
  if (trackingPoint?.period) return trackingPoint.period.name;
  if (!trackingPoint?.session) return null;
  return formatMessage({ id: REGISTER_SESSION_TOOLTIP_ID[trackingPoint.session] });
};

const GroupDayHeaderLabel: FC<{ column: AttendanceGridColumn }> = ({ column }) => {
  if (!column.date) {
    return <Typography noWrap>{column.label}</Typography>;
  }

  const date = parse(column.date, DEFAULT_DATE_FORMAT_FNS, newDateTimezoneOffset());
  return (
    <>
      <Typography color="common.grey" noWrap>
        {format(date, DATE_FORMAT_WEEK_DAY_MID_FNS).toUpperCase()}
      </Typography>
      <Typography noWrap>{format(date, DATE_FORMAT_SHORT_FNS)}</Typography>
    </>
  );
};

const DayHeaderSkeleton: FC = () => (
  <Stack alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
    <Skeleton width={48} height={12} />
    <Skeleton width={64} height={12} />
  </Stack>
);

const TrackingStatus: FC<{ complete: boolean; children: ReactNode }> = ({ complete, children }) => {
  const color = complete ? 'success.main' : 'common.orange';

  return (
    <>
      <Icon
        fontSize="small"
        sx={(theme) => ({
          color: theme.palette.background.paper,
          path: { color },
        })}
      >
        {complete ? <CheckIcon /> : <Attention2Icon />}
      </Icon>
      {/* Span - to avoid table hover color changes  */}
      <Typography component="span" color={color} noWrap>
        {children}
      </Typography>
    </>
  );
};

export const AttendanceGroupDayHeaderCell: FC<{ column: AttendanceGridColumn }> = ({ column }) => {
  if (column.is_loading) return <DayHeaderSkeleton />;

  return (
    <Stack alignItems="center" sx={{ width: '100%', height: '100%' }}>
      <GroupDayHeaderLabel column={column} />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={0.25}
        height={18}
        sx={{ width: '100%' }}
      >
        {!column.has_register ? (
          <Typography color="common.grey" noWrap>
            <FormattedMessage id="attendance-NoRegister" />
          </Typography>
        ) : (
          column.sessions.map((session) => (
            <Stack key={session.name} direction="row" alignItems="center" maxWidth={44} gap={0.25}>
              <TrackingStatus complete={session.complete}>
                <FormattedMessage id={session.name === 'am' ? 'attendance-AM' : 'attendance-PM'} />
              </TrackingStatus>
            </Stack>
          ))
        )}
      </Stack>
    </Stack>
  );
};

const TrackingPointTooltip: FC<{
  trackingPoint: AttendanceGridTrackingPoint | undefined;
  children: ReactElement;
}> = ({ trackingPoint, children }) => {
  const { formatMessage } = useIntl();

  return (
    <Tooltip
      title={
        <Typography color="common.grey2" variant="caption">
          {trackingPointTooltipTitle(trackingPoint, formatMessage)}
        </Typography>
      }
      arrow={false}
      componentsProps={{
        tooltip: {
          sx: (theme) => ({
            padding: theme.spacing(0.25, 0.5),
          }),
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

export const AttendanceSubjectGroupHeaderCell: FC<{ column: AttendanceGridColumn }> = ({
  column,
}) => {
  if (column.is_loading) return <DayHeaderSkeleton />;

  const trackingPoint = column.trackingPoint;

  return (
    <TrackingPointTooltip trackingPoint={trackingPoint}>
      <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
        <GroupDayHeaderLabel column={column} />
        {trackingPoint && (
          <Stack direction="row" alignItems="center" justifyContent="center" gap={0.25} height={18}>
            <TrackingStatus complete={trackingPoint.complete}>{trackingPoint.label}</TrackingStatus>
          </Stack>
        )}
      </Stack>
    </TrackingPointTooltip>
  );
};

export const AttendanceTrackingPointHeaderCell: FC<{ column: AttendanceGridColumn }> = ({
  column,
}) => (
  <TrackingPointTooltip trackingPoint={column.trackingPoint}>
    <Typography color="common.grey" sx={{ width: '100%', textAlign: 'center' }} noWrap>
      {column.label}
    </Typography>
  </TrackingPointTooltip>
);

export const AttendanceWeekdayHeaderCell: FC<{ column: AttendanceGridColumn }> = ({ column }) => (
  <Typography color="common.grey" sx={{ width: '100%', textAlign: 'center' }} noWrap>
    {column.label}
  </Typography>
);

const AttendanceCellContext = createContext<AttendanceCellContextValue | null>(null);

export const AttendanceCellContextProvider = AttendanceCellContext.Provider;

export const useAttendanceCellContext = (): AttendanceCellContextValue => {
  const context = useContext(AttendanceCellContext);
  if (!context) {
    throw new Error(
      'useAttendanceCellContext must be used within an AttendanceCellContextProvider',
    );
  }
  return context;
};
